// Colour helpers. All inputs are #RRGGBB strings.

export type RGB = [number, number, number];

export function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h.padEnd(6, '0').slice(0, 6);
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex([r, g, b]: RGB): string {
  return '#' + [r, g, b].map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0')).join('').toUpperCase();
}

/** Linear sRGB mix: `amount` of b into a (0 → a, 1 → b). */
export function mix(a: string, b: string, amount: number): string {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return rgbToHex([A[0] + (B[0] - A[0]) * amount, A[1] + (B[1] - A[1]) * amount, A[2] + (B[2] - A[2]) * amount]);
}

function channel(v: number): number {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

export function isDark(hex: string): boolean {
  return luminance(hex) < 0.22;
}

/** Shift towards black (positive) or white (negative) by `amount` (0–1). */
export function shade(hex: string, amount: number): string {
  return amount >= 0 ? mix(hex, '#000000', amount) : mix(hex, '#FFFFFF', -amount);
}

/** Pick a readable text colour on `bg`, preferring `preferred` when it has enough contrast. */
export function readableOn(bg: string, preferred: string, min = 3): string {
  if (contrast(bg, preferred) >= min) return preferred;
  return contrast(bg, '#171717') >= contrast(bg, '#FFFFFF') ? '#171717' : '#FFFFFF';
}

export function isHexColor(v: unknown): v is string {
  return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
}

export function toHsl(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l * 100];
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return [h, s * 100, l * 100];
}

export function fromHsl(h: number, s: number, l: number): string {
  const S = Math.max(0, Math.min(100, s)) / 100;
  const L = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const [r, g, b] = hh < 1 ? [c, x, 0] : hh < 2 ? [x, c, 0] : hh < 3 ? [0, c, x] : hh < 4 ? [0, x, c] : hh < 5 ? [x, 0, c] : [c, 0, x];
  const m = L - c / 2;
  return rgbToHex([(r + m) * 255, (g + m) * 255, (b + m) * 255]);
}

/** Add `n` to every channel (clamped) — the reference derives several chrome colours this way. */
export function offset(hex: string, n: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex([r + n, g + n, b + n]);
}
